import * as React from 'react';
import type {
  IShareButtonProps,
  IShareButtonsViewerProps,
} from '../../../types';
import shareButtonsStyles from './ShareButtons.module.scss';
import {
  defaultRootAriaLabel,
  noButtonsErrorMessage,
  shareUrlPrefixes,
  TestIds,
  TranslationKeys,
} from '../../../constants';
import ShareButton from './ShareButton';
import getFullShareUrl from '../../../utils/getFullShareUrl';
import getPlatformIdWithFallback from '../../../utils/getPlatformIdWithFallback';
import { getAccessibilityAttributesSpread } from '@wix/editor-elements-common-utils';

const ShareButtonsSkin: React.FC<IShareButtonsViewerProps> = props => {
  const {
    id,
    className,
    buttons,
    shareUrl,
    translations,
    onMouseEnter,
    onMouseLeave,
    lang,
    a11y,
  } = props;

  if (buttons.length === 0) {
    // eslint-disable-next-line no-console
    console.error(noButtonsErrorMessage);
  }

  const getShareUrlPrefix = (button: IShareButtonProps) => {
    const platformIdWithFallback = getPlatformIdWithFallback(
      button.platformId,
      button.value,
    );

    return shareUrlPrefixes[platformIdWithFallback] ?? button.value;
  };

  const visibleButtons = buttons.filter(btn => !btn.isHidden);
  const isSingleButtonMode = visibleButtons.length === 1;

  return (
    <div
      id={id}
      className={className}
      {...(isSingleButtonMode
        ? getAccessibilityAttributesSpread({ ariaAttributes: a11y })
        : {})}
      lang={lang}
    >
      <div
        className={shareButtonsStyles.root}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {isSingleButtonMode ? (
          <ShareButton
            {...visibleButtons[0]}
            key={`item_${visibleButtons[0].id}`}
            url={getFullShareUrl(
              getShareUrlPrefix(visibleButtons[0]),
              shareUrl,
            )}
            translations={translations}
          />
        ) : (
          // eslint-disable-next-line jsx-a11y/no-redundant-roles
          <ul
            id={`items_${id}`}
            className={shareButtonsStyles.container}
            data-testid={TestIds.buttonsContainer}
            aria-label={
              translations[TranslationKeys.ariaLabel] || defaultRootAriaLabel
            }
            {...getAccessibilityAttributesSpread({ ariaAttributes: a11y })}
            role="list"
          >
            {visibleButtons.map(button => (
              <li key={`item_${button.id}`} className={shareButtonsStyles.item}>
                <ShareButton
                  {...button}
                  url={getFullShareUrl(getShareUrlPrefix(button), shareUrl)}
                  translations={translations}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ShareButtonsSkin;
